import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  let filteredComics = comics.filter((c) => c.category === activeSlug);

  if (sortBy === 'popular') {
    filteredComics = [...filteredComics].sort((a, b) => b.views - a.views);
  } else if (sortBy === 'rating') {
    filteredComics = [...filteredComics].sort((a, b) => b.rating - a.rating);
  } else {
    filteredComics = [...filteredComics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  const sortOptions: { value: 'latest' | 'popular' | 'rating'; label: string }[] = [
    { value: 'latest', label: '最新更新' },
    { value: 'popular', label: '最受欢迎' },
    { value: 'rating', label: '评分最高' },
  ];

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'}漫画 - 43423漫画网`,
    description: currentCategory?.description || '漫画分类浏览',
    url: `https://43423mh.com/category/${activeSlug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '43423漫画网',
      url: 'https://43423mh.com',
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageJsonLd) }} />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10 md:pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category/rexue' },
              { label: currentCategory?.name || '' },
            ]}
          />

          <div className="mt-6">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              {currentCategory?.name}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{currentCategory?.description}</p>
          </div>

          <div className="mt-6 flex items-center flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  sortBy === opt.value
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-50 text-foreground-500 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {filteredComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-400">暂无该分类的漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}